import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getEstimate } from '../../services/estimateService';
import {
  Box,
  Tabs,
  Tab,
} from '@mui/material';
import type { Estimate } from '../../pages/EstimatesPage';
import EstimateInfoTab from './EstimateInfoTab';
import DetailedEstimateTab from './DetailedEstimateTab';
import { useEstimateData } from '../../contexts/EstimateDataContext';
import SimpleEstimateTab from './SimpleEstimateTab';
import RMSTab from './RMSTab';
import TesTab from './TesTab';
import PrzedmiarTab from './PrzedmiarTab';
import SummaryTab from './SummaryTab';

interface EstimateDetailsProps {
  estimate: Estimate;
  onBack: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`estimate-tabpanel-${index}`}
      aria-labelledby={`estimate-tab-${index}`}
      style={{ height: '100%' }}
      {...other}
    >
      {value === index && (
        <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', pt: '24px' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `estimate-tab-${index}`,
    'aria-controls': `estimate-tabpanel-${index}`,
  };
}

const EstimateDetails: React.FC<EstimateDetailsProps> = ({ estimate, onBack }) => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam ? Math.min(6, Math.max(0, parseInt(tabParam, 10) || 0)) : 0;
  const [tabValue, setTabValue] = useState(initialTab);
  const [currentEstimate, setCurrentEstimate] = useState<Estimate|undefined>(undefined);
  const [positions, setPositions] = useState<any[]>([]);
  const { setSectionData } = useEstimateData();
  // Cena łączna netto – liczona z pozycji (RMS + Kp/Kz/Z dla szczegółowych lub unit_price dla uproszczonych)
  const computeUnitPrice = (p:any):number => {
    // uproszczona pozycja – jeśli backend ma unit_price, użyj go
    if(p.position_type==='U'){
      return parseFloat(p.unit_price)||0;
    }
    // szczegółowa – policz z RMS
    const rmsArr = (p.rms||[]) as any[];
    const sumR = rmsArr.filter(r=>r.rms_type==='R').reduce((s,r)=> s + (parseFloat(r.norm)||0)*(parseFloat(r.rms_price)||0), 0);
    const sumM = rmsArr.filter(r=>r.rms_type==='M').reduce((s,r)=> s + (parseFloat(r.norm)||0)*(parseFloat(r.rms_price)||0), 0);
    const sumS = rmsArr.filter(r=>r.rms_type==='S').reduce((s,r)=> s + (parseFloat(r.norm)||0)*(parseFloat(r.rms_price)||0), 0);
    const kpPercent = (currentEstimate as any)?.kp ?? (currentEstimate as any)?.markup_kp ?? 0;
    const kzPercent = (currentEstimate as any)?.kz ?? (currentEstimate as any)?.markup_kz ?? 0;
    const zPercent  = (currentEstimate as any)?.z  ?? (currentEstimate as any)?.markup_z  ?? 0;
    const kpUnit = (kpPercent/100) * sumR;
    const kzUnit = (kzPercent/100) * sumM;
    const zUnit  = (zPercent/100)  * (sumR+sumM+sumS+kpUnit+kzUnit);
    return sumR+sumM+sumS+kpUnit+kzUnit+zUnit;
  };
  const wkr = React.useMemo(()=>{
    return (positions||[]).reduce((sum:number, p:any)=>{
      const qty = parseFloat(p.quantity)||0;
      return sum + computeUnitPrice(p)*qty;
    }, 0);
  }, [positions]);

  const mapHeader = (h:any): Estimate => ({
    id: h.id,
    estimateNumber: h.estimate_number,
    name: h.name,
    objectName: h.object_name,
    client: h.client,
    clientAddress: h.client_address,
    objectAddress: h.object_address,
    createdAt: h.prepared_at,
    updatedAt: h.updated_at,
    kp: Number.isFinite(+h.markup_kp) ? +h.markup_kp : 0,
    kz: Number.isFinite(+h.markup_kz) ? +h.markup_kz : 0,
    z:  Number.isFinite(+h.markup_z) ? +h.markup_z : 0,
    rg: Number.isFinite(+h.rg) ? +h.rg : 0,
    vat: Number.isFinite(+h.vat) ? +h.vat : 0,
    status: h.status==='finished' ? 'completed' : (h.status==='draft' ? 'draft' : 'active'),
    settings: {
      currency: h.currency || 'zł',
      precisionUnitPrice: h.precision_unit_price ?? 2,
      precisionRmsPrice: h.precision_rms_price ?? 3,
      precisionQuantity: h.precision_quantity ?? 3,
      precisionExpenses: h.precision_expenses ?? 4,
      precisionNorm: h.precision_norm ?? 6,
    }
  } as any);

  const refreshEstimate = async () => {
    try {
      const fresh = await getEstimate(estimate.id);
      const mapped = mapHeader(fresh) as any;
      setCurrentEstimate(mapped);
      setPositions(fresh.positions || []);
      // Uzupełnij context dla TES natychmiast po pobraniu danych – bez czekania na montaż zakładki „Szczegółowy”
      setSectionData('detailedRows', fresh.positions || []);
      // titleInfo i summary dla generowania Google Sheet
      const netto = (fresh.positions || []).reduce((s: number, p: any) => s + (parseFloat(p.value) || 0), 0);
      const vatPct = (mapped?.vat ?? 0) / 100;
      setSectionData('titleInfo', {
        projectName: mapped?.name || fresh.name,
        objectName: mapped?.objectName || fresh.object_name,
        orderingParty: mapped?.client || fresh.client,
        date: mapped?.createdAt || new Date().toISOString().slice(0, 10),
        indirectPercent: mapped?.kp ?? 0,
        purchasePercent: mapped?.kz ?? 0,
        profitPercent: mapped?.z ?? 0,
        laborRate: mapped?.rg ?? 0,
        vatPercent: (mapped?.vat ?? 23),
        netto,
        brutto: netto * (1 + vatPct)
      });
      setSectionData('summary', { netto, vatRate: vatPct || 0.23 });
    } catch (err) {
      console.error('Nie udało się odświeżyć kosztorysu:', err);
    }
  };

  // Pobierz pełny kosztorys przy pierwszym wyświetleniu
  useEffect(() => {
    refreshEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function refetchEstimate() {
      const fresh = await getEstimate(estimate.id);
      setCurrentEstimate(mapHeader(fresh));
      setPositions(fresh.positions || []);
    }
    refetchEstimate();
  }, [estimate.id, tabValue]);

  // Zablokuj przewijanie strony, aby scroll działał tylko w tabeli
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Global blokada przewijania strony klawiszami (ArrowUp/Down, PgUp/PgDn, Space)
  useEffect(() => {
    const preventWindowScroll = (e: KeyboardEvent) => {
      const keys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
      if (keys.includes(e.key)) {
        const tag = (e.target as HTMLElement).tagName;
        if (tag !== 'INPUT' && tag !== 'TEXTAREA' && !(e.target as HTMLElement).isContentEditable) {
          e.preventDefault();
        }
      }
    };
    window.addEventListener('keydown', preventWindowScroll, { passive: false });
    return () => window.removeEventListener('keydown', preventWindowScroll);
  }, []);

  // Skróty Option+↑ / Option+↓ do przełączania kart
  useEffect(() => {
    const handleTabShortcuts = (e: KeyboardEvent) => {
      if (!e.altKey) return; // wymagana opcja/alt
      if (e.key === 'ArrowDown') {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) return;
        e.preventDefault();
        setTabValue(prev => Math.min(prev + 1, 6)); // 6 = ostatnia karta
      }
      if (e.key === 'ArrowUp') {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) return;
        e.preventDefault();
        setTabValue(prev => Math.max(prev - 1, 0));
      }
    };
    window.addEventListener('keydown', handleTabShortcuts);
    return () => window.removeEventListener('keydown', handleTabShortcuts);
  }, []);

  // Skrót klawiszowy § do przełączania kart (bez Shift = do przodu, z Shift = do tyłu)
  // Używamy capture phase, aby przechwycić zdarzenie przed innymi listenerami w zakładkach
  useEffect(() => {
    const handleSectionKey = (e: KeyboardEvent) => {
      // Sprawdź czy to klawisz § - używamy wielu sposobów wykrywania
      // e.code reprezentuje fizyczny klawisz i działa niezależnie od Shift
      // e.key może się zmieniać (np. '§' bez Shift, '£' z Shift)
      const isSectionKey = 
        e.code === 'IntlRo' || // fizyczny klawisz § na większości klawiatur (działa z Shift i bez)
        e.code === 'IntlYen' || // alternatywny kod
        (e.code === 'Digit6' && e.shiftKey && e.key === '§') || // niektóre klawiatury: Shift+6 = §
        e.key === '§' || // bez Shift
        e.key === '£' || // Shift+§ może generować £
        e.key === '±' || // może być z Shift
        e.keyCode === 193 || // alternatywny sposób
        (e.keyCode === 54 && e.altKey && !e.ctrlKey && !e.metaKey) || // Option+6 na macOS
        (e.keyCode === 187 && !e.altKey && !e.ctrlKey && !e.metaKey); // niektóre mapowania
      
      if (!isSectionKey) return;
      
      // Sprawdź czy użytkownik nie jest w polu edycji
      const target = e.target as HTMLElement;
      const tag = target.tagName;
      const isEditable = 
        tag === 'INPUT' || 
        tag === 'TEXTAREA' || 
        target.isContentEditable ||
        target.getAttribute('role') === 'textbox' ||
        target.getAttribute('contenteditable') === 'true';
      
      if (isEditable) return;
      
      // Zatrzymaj propagację i domyślne zachowanie PRZED sprawdzeniem Shift
      // stopImmediatePropagation() zapobiega przechwytywaniu przez inne handlery
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();
      
      // Sprawdź Shift po wykryciu klawisza §
      if (e.shiftKey) {
        // Shift+§ - przejście do tyłu (poprzednia zakładka, cyklicznie)
        setTabValue(prev => prev === 0 ? 6 : prev - 1);
      } else {
        // § - przejście do przodu (następna zakładka, cyklicznie)
        setTabValue(prev => prev === 6 ? 0 : prev + 1);
      }
    };
    
    // Używamy capture phase na window i document, aby przechwycić zdarzenie przed innymi listenerami
    window.addEventListener('keydown', handleSectionKey, { capture: true });
    document.addEventListener('keydown', handleSectionKey, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleSectionKey, { capture: true });
      document.removeEventListener('keydown', handleSectionKey, { capture: true });
    };
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!currentEstimate) return <div>Ładowanie...</div>;

  // Handler dla skrótu klawiszowego § bezpośrednio na kontenerze
  const handleContainerKeyDown = (e: React.KeyboardEvent) => {
    // Sprawdź czy to klawisz § - sprawdzamy najpierw e.code (fizyczny klawisz)
    // e.code działa niezależnie od Shift, więc jest najbardziej niezawodne
    // e.key może się zmieniać (np. '§' bez Shift, '£' z Shift)
    const isSectionKey = 
      e.code === 'IntlRo' || // fizyczny klawisz § (najbardziej niezawodne, działa z Shift i bez)
      e.code === 'IntlYen' ||
      (e.code === 'Digit6' && e.shiftKey && e.key === '§') || // niektóre klawiatury: Shift+6 = §
      e.key === '§' || // bez Shift
      e.key === '£' || // Shift+§ może generować £
      e.key === '±' || // może być z Shift
      e.keyCode === 193 || // alternatywny sposób (działa z Shift i bez)
      (e.keyCode === 54 && e.altKey && !e.ctrlKey && !e.metaKey) || // Option+6
      (e.keyCode === 187 && !e.altKey && !e.ctrlKey && !e.metaKey); // niektóre mapowania
    
    if (!isSectionKey) return;
    
    const target = e.target as HTMLElement;
    const tag = target.tagName;
    const isEditable = 
      tag === 'INPUT' || 
      tag === 'TEXTAREA' || 
      target.isContentEditable ||
      target.getAttribute('role') === 'textbox' ||
      target.getAttribute('contenteditable') === 'true';
    
    if (isEditable) return;
    
    // Zatrzymaj propagację i domyślne zachowanie
    e.stopPropagation();
    e.preventDefault();
    
    // Sprawdź Shift po wykryciu klawisza §
    if (e.shiftKey) {
      // Shift+§ - przejście do tyłu (poprzednia zakładka, cyklicznie)
      setTabValue(prev => prev === 0 ? 6 : prev - 1);
    } else {
      // § - przejście do przodu (następna zakładka, cyklicznie)
      setTabValue(prev => prev === 6 ? 0 : prev + 1);
    }
  };

  return (
    <Box 
      sx={{ 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden' // zapobiega przewijaniu strony
      }}
      onKeyDown={handleContainerKeyDown}
      tabIndex={-1}
    >
      {/* Tab Content - Full Height minus bottom bar */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: '48px' // Space for bottom bar
      }}>
        <TabPanel value={tabValue} index={0}>
          <EstimateInfoTab estimate={currentEstimate!} onRefresh={refreshEstimate} onBack={onBack} />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <TesTab estimate={currentEstimate!} />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <PrzedmiarTab estimateId={currentEstimate.id} onImported={refreshEstimate} />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <DetailedEstimateTab estimate={currentEstimate!} positions={positions} onRefresh={refreshEstimate} onBack={onBack} />
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <SimpleEstimateTab estimate={currentEstimate!} positions={positions} />
        </TabPanel>
        <TabPanel value={tabValue} index={5}>
          <RMSTab estimate={currentEstimate!} positions={positions} />
        </TabPanel>
        <TabPanel value={tabValue} index={6}>
          <SummaryTab summary={{ netto: wkr }} />
        </TabPanel>
      </Box>

      {/* Bottom Bar - Navigation and Tabs */}
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        right: 0,
        left: 0, // dochodzi do sidebar
        backgroundColor: '#EDEDFF', // RGB(0.93, 0.93, 1.0)
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pl: 'calc(40px + 12px + 24px + 16px)', // sidebar (40px) + margin (12px) + padding Layout (24px) + paddingLeft pierwszej komórki (16px) = wyrównanie z lewą krawędzią tabeli
        pr: 2,
        height: '40px',
        borderTop: 'none'
      }}>
        {/* Left side - Tabs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-start' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="estimate tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: '40px',
            '& .MuiTab-root': {
              minHeight: '40px',
              textTransform: 'none',
              fontSize: '14px',
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 400,
              minWidth: 'auto',
              px: 1,
              py: 0,
              mx: 0.5,
              color: '#020242',
              borderBottom: 'none',
              position: 'relative',
              '&.Mui-selected': {
                color: '#020242',
                fontWeight: 500,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '6px',
                  left: '8px',
                  right: '8px',
                  height: '2px',
                  backgroundColor: '#020242'
                }
              },
              '&:hover': {
                backgroundColor: 'transparent'
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none' // Ukryj standardowy indicator
            }
          }}
        >
          <Tab label="Karta Info" {...a11yProps(0)} />
          <Tab label="TES" {...a11yProps(1)} />
          <Tab label="Przedmiar" {...a11yProps(2)} />
          <Tab label="Kosztorys Szczegółowy" {...a11yProps(3)} />
          <Tab label="Kosztorys Uproszczony" {...a11yProps(4)} />
          <Tab label="RMS" {...a11yProps(5)} />
          <Tab label="Podsumowanie" {...a11yProps(6)} />
        </Tabs>
        
        </Box>
        {/* Right side - Sum */}
        <Box sx={{ 
          ml: 'auto',
          fontWeight: 500, 
          color: '#c53030', // Kolor jak UNITRESCO
          fontSize: '14px', 
          fontFamily: 'Roboto, sans-serif',
          whiteSpace: 'nowrap', 
          textAlign: 'right',
          paddingRight: '16px', // Wyrównanie z paddingiem ostatniej kolumny tabeli
          marginRight: '65px' // Przesunięcie w lewo o 65px (było 70px, teraz +5px w prawo)
        }}>
          Wartość netto: {wkr.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
        </Box>
      </Box>
    </Box>
  );
};

export default EstimateDetails;
